// React
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// Servisni sloj
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { getTenantAPI, searchTenantUsersAPI } from "services/api/tenants/api";
import { impersonateUserAPI } from "services/api/users/api";
import { Tenant, TenantUserRow } from "services/api/tenants/types";
import { Pagination } from "services/search";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";
import { Button } from "ui/components/Button/Button";
import { NoResults } from "ui/components/NoResults";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import {
  TENANT_DETAIL_PAGE_TITLE,
  TENANT_SEARCH_PAGE_TITLE,
} from "ui/components/PageTitle/consts";
import { TableSearch } from "ui/components/Table/TableSearch";
import { TextField } from "ui/components/TextField/TextField";

// Moduli specifični za projekt
import { Routes } from "ui/modules/navigation";
import UserEditCard from "ui/modules/users/pages/UserDetailPage/components/UserDetailCard/components/UserEditCard/UserEditCard";
import TabMenu from "../TabMenu/TabMenu";

// Lokalne komponente
import UserPasswordEditCard from "./components/UserPasswordEditCard/UserPasswordEditCard";

// Tipovi i konstante
import { TENANT_USERS_COLUMNS } from "./consts";
import { TenantUsersPageCmp, TenantUsersPageProps } from "./types";

/** Korisnici jednog klijenta, s pretragom i promjenom lozinke. */
const TenantUsersPage: TenantUsersPageCmp = (_props: TenantUsersPageProps) => {
  const { tenantId } = useParams<{ tenantId: string }>();

  const [tenant, setTenant] = useState<Tenant>();
  const [searchResults, setSearchResults] =
    useState<DataWithPagination<TenantUserRow>>(initialDataWithPagination);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [query, setQuery] = useState<string>("");

  const [passwordUser, setPasswordUser] = useState<TenantUserRow | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);

  const fetchTenant = useCallback(async () => {
    if (!tenantId) return;

    try {
      setTenant(await getTenantAPI(tenantId));
    } catch {
      // greška se prikazuje kroz snackbar
    }
  }, [tenantId]);

  const fetchUsers = useCallback(
    async (pagination: Pagination, name: string) => {
      if (!tenantId) return;

      setIsLoading(true);

      try {
        setSearchResults(await searchTenantUsersAPI(tenantId, pagination, name || null));
      } catch {
        setSearchResults(initialDataWithPagination);
      } finally {
        setIsLoading(false);
      }
    },
    [tenantId]
  );

  useEffect(() => {
    fetchTenant();
  }, [fetchTenant]);

  /*
    Pretraga se pokreće s odgodom da svaka tipkana slova ne šalju zahtjev; `query` je jedini
    okidač, pa se popis osvježi i kad se polje isprazni.
  */
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers(initialDataWithPagination.pagination, query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query, fetchUsers]);

  const handleQueryChange = useCallback((e: any) => {
    setQuery(e?.target?.value ?? "");
  }, []);

  const handlePaginationChange = useCallback(
    (pagination: Pagination) => {
      fetchUsers(pagination, query);
    },
    [fetchUsers, query]
  );

  /*----------------------------------------------------------------
      DODAVANJE KORISNIKA
  ----------------------------------------------------------------*/

  const handleCreateClick = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const handleCreateClose = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  const handleCreateSuccess = useCallback(() => {
    setShowCreateModal(false);
    fetchUsers(initialDataWithPagination.pagination, query);
  }, [fetchUsers, query]);

  /*----------------------------------------------------------------
      LOZINKA
  ----------------------------------------------------------------*/

  const handlePasswordClick = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>, id: string) => {
      event.stopPropagation();
      event.preventDefault();

      const user = searchResults.data.find((item) => item.id === id);

      if (!user) return;

      setPasswordUser(user);
      setShowPasswordModal(true);
    },
    [searchResults.data]
  );

  const handlePasswordClose = useCallback(() => {
    setShowPasswordModal(false);
    setPasswordUser(null);
  }, []);

  const handlePasswordSuccess = useCallback(() => {
    setShowPasswordModal(false);
    setPasswordUser(null);
  }, []);

  /*----------------------------------------------------------------
      PRIJAVA KAO KORISNIK

      Aplikacija se otvara u novoj kartici da admin ostane otvoren;
      kod vrijedi minutu i troši se pri otvaranju.
  ----------------------------------------------------------------*/

  const handleImpersonateClick = useCallback(
    async (event: React.MouseEvent<HTMLSpanElement>, id: string) => {
      event.stopPropagation();
      event.preventDefault();

      try {
        const code = await impersonateUserAPI(id);

        if (!code) return;

        window.open(
          `${import.meta.env.VITE_APP_URL}/impersonate?code=${encodeURIComponent(code)}`,
          "_blank",
          "noopener,noreferrer"
        );
      } catch {
        // greška se prikazuje kroz snackbar
      }
    },
    []
  );

  const title = TENANT_DETAIL_PAGE_TITLE;

  const breadcrumbs: BreadcrumbItem[] = [
    { text: TENANT_SEARCH_PAGE_TITLE.text, route: TENANT_SEARCH_PAGE_TITLE.route },
  ];

  const tenantName = tenant
    ? tenant.companyName || [tenant.firstName, tenant.lastName].filter(Boolean).join(" ")
    : "";

  if (!tenantId) return <></>;

  const isEmpty = !isLoading && searchResults.data.length === 0;

  return (
    <div>
      <div className="bg-white rounded-t-md">
        <PageHeader>
          <div className="flex items-center justify-between">
            <div>
              <PageTitle
                title={tenantName ? `${title.text} [${tenantName}]` : title.text}
                icon={title.icon}
                iconColor={title.iconColor}
              />
              <Breadcrumb items={breadcrumbs} />
            </div>

            <Button
              variant="primary"
              children="Dodaj korisnika"
              startIcon="/media/icons/plus.svg"
              onClick={handleCreateClick}
            />
          </div>
        </PageHeader>
        <TabMenu tenantId={tenantId} />
      </div>

      {/* Jedna ploha, bez kartica i bez crte između filtra i tablice. */}
      <div>
        <div className="p-4">
          <div className="w-full md:w-[320px]">
            <TextField
              type="text"
              name="query"
              label="Pretraga"
              placeholder="Ime, prezime ili email"
              value={query}
              onChange={handleQueryChange}
              width="full"
            />
          </div>
        </div>

        <div className="p-4">
          {isEmpty ? (
            <NoResults
              message={
                query
                  ? "Nijedan korisnik ne odgovara pretrazi"
                  : "Ovaj klijent nema nijednog korisnika"
              }
            />
          ) : (
            <TableSearch
              columns={TENANT_USERS_COLUMNS(handlePasswordClick, handleImpersonateClick)}
              data={searchResults.data}
              selectableRows={false}
              pagination={searchResults.pagination}
              onPaginationChange={handlePaginationChange}
              isLoading={isLoading}
            />
          )}
        </div>
      </div>

      <UserEditCard
        userId={null}
        tenantId={tenantId}
        show={showCreateModal}
        onSuccess={handleCreateSuccess}
        onClose={handleCreateClose}
      />

      {passwordUser && (
        <UserPasswordEditCard
          show={showPasswordModal}
          userId={passwordUser.id}
          userName={
            [passwordUser.firstName, passwordUser.lastName].filter(Boolean).join(" ") ||
            passwordUser.email ||
            ""
          }
          onSuccess={handlePasswordSuccess}
          onClose={handlePasswordClose}
        />
      )}
    </div>
  );
};

TenantUsersPage.route = Routes.TenantUsersPageRouter;

export default TenantUsersPage;
